import { Trans } from "@lingui/react/macro";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import {
  type PatientMonitoringType,
  type QuestionnaireTypes,
  type RelativeDeltaModel,
  apiPostMonitoringSettingsQuestionnaires,
} from "../../../api";
import { Button } from "../../../components/ui-kit/button";
import { Dialog } from "../../../components/ui-kit/dialog";
import { Form } from "../../../components/ui-kit/form/form";
import { assert } from "../../../utils/utils";
import { AdminConsole } from "../admin-console";
import { QuestionnaireSettingsForm } from "./questionnaire-settings-form";

export const AddQuestionnaireSettingsDialog = observer(
  function AddQuestionnaireSettingsDialog({
    admin,
    onClose,
    onSaved,
  }: {
    admin: AdminConsole;
    onClose: () => void;
    onSaved: () => void;
  }) {
    const [fields] = useState(() =>
      observable({
        questionnaire_id: null as QuestionnaireTypes | null,
        monitoring_type: null as PatientMonitoringType | null,
        country: null as string | null,
        region: null as number | null,
        first_when_active: false,
        skippable: false,
        filling_frequency: [],
        first_after_active: null as RelativeDeltaModel | null,
        stop_after_n: null as number | null,
        stop_after_time_from_first: null as RelativeDeltaModel | null,
        clinic_uuid: null as string | null,
        physician_uuid: null as string | null,
      }),
    );

    async function save() {
      const { questionnaire_id, monitoring_type } = fields;
      assert(questionnaire_id != null, "Questionnaire is required");
      assert(monitoring_type != null, "Monitoring type is required");

      await apiPostMonitoringSettingsQuestionnaires({
        body: { ...fields, questionnaire_id, monitoring_type },
      });
      admin.api.clearResponseCache();
      onSaved();
      onClose();
    }

    return (
      <Dialog open onOpenChange={(open) => !open && onClose()} width="672px">
        <h3 className="mb-6 text-base font-bold">
          <Trans>Add new questionnaire settings</Trans>
        </h3>
        <Form className="flex flex-col min-h-0 flex-1" onSubmit={() => save()}>
          <div className="overflow-y-auto min-h-0 px-1 py-1">
            <QuestionnaireSettingsForm
              fields={fields}
              admin={admin}
              mode="create"
            />
          </div>

          <div className="mt-6 flex justify-center gap-2">
            <Button type="submit">
              <Trans>Add questionnaire settings</Trans>
            </Button>
            <Button
              type="button"
              variant="subtle"
              size="default"
              onClick={onClose}
            >
              <Trans>Cancel</Trans>
            </Button>
          </div>
        </Form>
      </Dialog>
    );
  },
);
